import { isRecord } from './errors'

export interface DashboardLocalizedTextMap {
  readonly [languageCode: string]: string | undefined
}

export interface DashboardBookPdfReference {
  readonly assetId: string
  readonly contentType: 'application/pdf'
  readonly fileName?: string
  readonly sizeBytes?: number
  readonly uploadedAt?: string | number
}

export interface DashboardBook {
  readonly enableAudioBook?: string | boolean
  readonly enableOnlineBook?: string | boolean
  readonly featuredImage?: string
  readonly id: string
  readonly language?: string
  readonly bookPdf?: DashboardBookPdfReference | null
  readonly sort?: number
  readonly status?: string
  readonly title?: string | DashboardLocalizedTextMap
  readonly translate?: Record<string, string>
  readonly workbookPdf?: DashboardBookPdfReference | null
}

export function isPublishedBookFeature(value: unknown) {
  if (value === true) {
    return true
  }

  if (typeof value !== 'string') {
    return false
  }

  return ['published', 'true', '1', 'enabled', 'enable'].includes(
    value.trim().toLowerCase(),
  )
}

export interface DashboardActivationKey {
  readonly active?: boolean | string
  readonly createdAt?: number
  readonly duration?: number
  readonly endAt?: number
  readonly id: string
  readonly keyNumber?: string
  readonly startAt?: number
  readonly status?: 'active' | 'expired' | 'inactive' | 'unknown'
  readonly type?: string
}

export interface DashboardUserProfile {
  readonly accent_default?: string
  readonly activation_key?: DashboardActivationKey | null
  readonly activationCode?: string
  readonly about?: string
  readonly createdAt?: number
  readonly count_daily_activities?: number
  readonly country?: string
  readonly current_learning_program?: string
  readonly daily_activities?: Array<{
    readonly createdAt: number
    readonly id?: string
    readonly seconds?: number
    readonly userId?: string
  }>
  readonly email?: string | null
  readonly id: string
  readonly imageUrl?: string
  readonly language_default?: string
  readonly learnLanguages?: Array<{
    readonly language: string
    readonly primary?: boolean
  }>
  readonly my_daily_goal?: string[]
  readonly phone?: string
  readonly region?: string
  readonly firstname?: string
  readonly lastname?: string
  readonly middlename?: string
  readonly personalization?: Record<string, unknown> | null
  readonly progress_lessons?: Array<{
    readonly bookId?: string
    readonly lessonId: string
    readonly progress?: number
  }>
  readonly progress_lessons_by_id?: Record<
    string,
    | {
        readonly bookId?: string
        readonly lessonId: string
        readonly progress?: number
      }
    | undefined
  >
  readonly progress_workbook_lessons?: Array<{
    readonly bookId?: string
    readonly lessonId: string
    readonly progress?: number
    readonly rating?: string
  }>
  readonly progress_workbook_lessons_by_id?: Record<
    string,
    | {
        readonly bookId?: string
        readonly lessonId: string
        readonly progress?: number
        readonly rating?: string
      }
    | undefined
  >
  readonly selfstudy_chapter_learning?: {
    readonly current_chapter?: string
    readonly id: string
    readonly learning_program?: string
    readonly userId?: string
  } | null
  readonly username?: string
}

function normalizeStringArray(value: unknown) {
  if (!Array.isArray(value)) {
    return []
  }

  return value.filter(
    (entry): entry is string => typeof entry === 'string' && entry.trim().length > 0,
  )
}

function normalizeBookPdfReference(value: unknown): DashboardBookPdfReference | null {
  if (!isRecord(value) || typeof value.assetId !== 'string' || !/^[a-f0-9]{32}$/.test(value.assetId)) {
    return null
  }

  return {
    assetId: value.assetId,
    contentType: 'application/pdf',
    ...(typeof value.fileName === 'string' ? { fileName: value.fileName } : {}),
    ...(typeof value.sizeBytes === 'number' && Number.isSafeInteger(value.sizeBytes) && value.sizeBytes > 0
      ? { sizeBytes: value.sizeBytes }
      : {}),
    ...(typeof value.uploadedAt === 'string' || typeof value.uploadedAt === 'number'
      ? { uploadedAt: value.uploadedAt }
      : {}),
  }
}

export function normalizeDashboardBook(book: DashboardBook): DashboardBook {
  return {
    ...book,
    enableAudioBook: normalizeBookFeatureStatus(book.enableAudioBook),
    enableOnlineBook: normalizeBookFeatureStatus(book.enableOnlineBook),
    bookPdf: normalizeBookPdfReference(book.bookPdf),
    workbookPdf: normalizeBookPdfReference(book.workbookPdf),
  }
}

function normalizeBookFeatureStatus(value: unknown): string | undefined {
  if (isPublishedBookFeature(value)) {
    return 'published'
  }

  if (value === false) {
    return undefined
  }

  return typeof value === 'string' ? value.trim() || undefined : undefined
}

function normalizeProgressPercent(value: unknown) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined
  }

  return Math.max(0, Math.min(100, Math.round(value)))
}

function normalizeActivationKey(
  value: DashboardUserProfile['activation_key'],
): DashboardUserProfile['activation_key'] {
  if (!isRecord(value) || typeof value.id !== 'string' || value.id.length === 0) {
    return null
  }

  return {
    active:
      typeof value.active === 'boolean' || typeof value.active === 'string'
        ? value.active
        : undefined,
    createdAt: typeof value.createdAt === 'number' ? value.createdAt : undefined,
    duration: typeof value.duration === 'number' ? value.duration : undefined,
    endAt: typeof value.endAt === 'number' ? value.endAt : undefined,
    id: value.id,
    keyNumber: typeof value.keyNumber === 'string' ? value.keyNumber : undefined,
    startAt: typeof value.startAt === 'number' ? value.startAt : undefined,
    status:
      value.status === 'active' ||
      value.status === 'expired' ||
      value.status === 'inactive' ||
      value.status === 'unknown'
        ? value.status
        : undefined,
  }
}

export function isDashboardActivationKeyActive(
  activationKey: DashboardActivationKey | null | undefined,
) {
  if (!activationKey) {
    return false
  }

  if (
    typeof activationKey.endAt === 'number' &&
    activationKey.endAt <= Date.now()
  ) {
    return false
  }

  if (activationKey.status) {
    return activationKey.status === 'active'
  }

  return (
    (activationKey.active === true || activationKey.active === 'activated') &&
    typeof activationKey.endAt === 'number' &&
    activationKey.endAt > Date.now()
  )
}

export function getDashboardActivationRenewalStartAt(
  activationKey: DashboardActivationKey | null | undefined,
  fallbackStartAt = Date.now(),
) {
  const endAt = activationKey?.endAt

  return typeof endAt === 'number' && endAt > fallbackStartAt
    ? endAt
    : fallbackStartAt
}

function normalizeLearnLanguages(
  value: DashboardUserProfile['learnLanguages'],
): NonNullable<DashboardUserProfile['learnLanguages']> {
  if (!Array.isArray(value)) {
    return []
  }

  return value.filter(
    (entry): entry is NonNullable<DashboardUserProfile['learnLanguages']>[number] =>
      isRecord(entry) &&
      typeof entry.language === 'string' &&
      entry.language.trim().length > 0,
  )
}

function normalizeDailyActivities(
  value: DashboardUserProfile['daily_activities'],
): NonNullable<DashboardUserProfile['daily_activities']> {
  if (!Array.isArray(value)) {
    return []
  }

  return value.filter(
    (entry): entry is NonNullable<DashboardUserProfile['daily_activities']>[number] =>
      isRecord(entry) && typeof entry.createdAt === 'number',
  )
}

type LessonProgressEntry = NonNullable<
  DashboardUserProfile['progress_lessons']
>[number]
type WorkbookProgressEntry = NonNullable<
  DashboardUserProfile['progress_workbook_lessons']
>[number]

function normalizeLessonProgressEntry(value: unknown): LessonProgressEntry | null {
  if (!isRecord(value) || typeof value.lessonId !== 'string' || value.lessonId.length === 0) {
    return null
  }

  const progress = normalizeProgressPercent(value.progress)

  return {
    ...(typeof value.bookId === 'string' ? { bookId: value.bookId } : {}),
    lessonId: value.lessonId,
    ...(typeof progress === 'number' ? { progress } : {}),
  }
}

function normalizeWorkbookProgressEntry(value: unknown): WorkbookProgressEntry | null {
  if (!isRecord(value) || typeof value.lessonId !== 'string' || value.lessonId.length === 0) {
    return null
  }

  const progress = normalizeProgressPercent(value.progress)

  return {
    ...(typeof value.bookId === 'string' ? { bookId: value.bookId } : {}),
    lessonId: value.lessonId,
    ...(typeof progress === 'number' ? { progress } : {}),
    ...(typeof value.rating === 'string' ? { rating: value.rating } : {}),
  }
}

function normalizeLessonProgressList(value: unknown): LessonProgressEntry[] {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map(normalizeLessonProgressEntry)
    .filter((entry): entry is LessonProgressEntry => entry !== null)
}

function normalizeWorkbookProgressList(value: unknown): WorkbookProgressEntry[] {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map(normalizeWorkbookProgressEntry)
    .filter((entry): entry is WorkbookProgressEntry => entry !== null)
}

function normalizeLessonProgressMap(value: unknown): LessonProgressEntry[] {
  if (!isRecord(value)) {
    return []
  }

  return Object.values(value)
    .map(normalizeLessonProgressEntry)
    .filter((entry): entry is LessonProgressEntry => entry !== null)
}

function normalizeWorkbookProgressMap(value: unknown): WorkbookProgressEntry[] {
  if (!isRecord(value)) {
    return []
  }

  return Object.values(value)
    .map(normalizeWorkbookProgressEntry)
    .filter((entry): entry is WorkbookProgressEntry => entry !== null)
}

function mergeLessonProgressEntries({
  legacyEntries,
  mappedEntries,
}: {
  readonly legacyEntries: LessonProgressEntry[]
  readonly mappedEntries: LessonProgressEntry[]
}) {
  const entriesByLessonId = new Map<string, LessonProgressEntry>()

  legacyEntries.forEach((entry) => entriesByLessonId.set(entry.lessonId, entry))
  mappedEntries.forEach((entry) => entriesByLessonId.set(entry.lessonId, entry))

  return Array.from(entriesByLessonId.values())
}

function getWorkbookProgressKey(entry: WorkbookProgressEntry) {
  return JSON.stringify([entry.bookId ?? null, entry.lessonId])
}

function mergeWorkbookProgressEntries({
  legacyEntries,
  mappedEntries,
}: {
  readonly legacyEntries: WorkbookProgressEntry[]
  readonly mappedEntries: WorkbookProgressEntry[]
}) {
  const entriesByWorkbookLessonId = new Map<string, WorkbookProgressEntry>()

  legacyEntries.forEach((entry) =>
    entriesByWorkbookLessonId.set(getWorkbookProgressKey(entry), entry),
  )
  mappedEntries.forEach((entry) =>
    entriesByWorkbookLessonId.set(getWorkbookProgressKey(entry), entry),
  )

  return Array.from(entriesByWorkbookLessonId.values())
}

function normalizeSelfStudyChapterLearning(
  value: DashboardUserProfile['selfstudy_chapter_learning'],
): DashboardUserProfile['selfstudy_chapter_learning'] {
  if (!isRecord(value) || typeof value.id !== 'string' || value.id.length === 0) {
    return null
  }

  return {
    current_chapter:
      typeof value.current_chapter === 'string' ? value.current_chapter : undefined,
    id: value.id,
    learning_program:
      typeof value.learning_program === 'string' ? value.learning_program : undefined,
    userId: typeof value.userId === 'string' ? value.userId : undefined,
  }
}

function normalizePersonalization(
  value: DashboardUserProfile['personalization'],
): DashboardUserProfile['personalization'] {
  if (!isRecord(value)) {
    return null
  }

  const legacySelfLearning = Array.isArray(value.selfLearning)
    ? value.selfLearning.filter((entry) => isRecord(entry))
    : []
  const mappedSelfLearning = isRecord(value.selfLearningByStepId)
    ? Object.values(value.selfLearningByStepId).filter((entry) => isRecord(entry))
    : []

  if (mappedSelfLearning.length === 0) {
    return value
  }

  const selfLearningByStepId = new Map<string, Record<string, unknown>>()
  const addSelfLearningEntry = (entry: Record<string, unknown>) => {
    const chapterId = typeof entry.chapterId === 'string' ? entry.chapterId : ''
    const selfStudyId =
      typeof entry.selftStudyId === 'string' ? entry.selftStudyId : ''

    if (!chapterId || !selfStudyId) {
      return
    }

    selfLearningByStepId.set(JSON.stringify([chapterId, selfStudyId]), entry)
  }

  legacySelfLearning.forEach(addSelfLearningEntry)
  mappedSelfLearning.forEach(addSelfLearningEntry)

  return {
    ...value,
    selfLearning: Array.from(selfLearningByStepId.values()),
  }
}

export function normalizeDashboardUserProfile(
  profile: DashboardUserProfile | null,
): DashboardUserProfile | null {
  if (!profile) {
    return null
  }

  return {
    ...profile,
    about: typeof profile.about === 'string' ? profile.about : undefined,
    accent_default:
      typeof profile.accent_default === 'string' ? profile.accent_default : undefined,
    activation_key: normalizeActivationKey(profile.activation_key),
    activationCode:
      typeof profile.activationCode === 'string' ? profile.activationCode : undefined,
    count_daily_activities:
      typeof profile.count_daily_activities === 'number'
        ? profile.count_daily_activities
        : undefined,
    country: typeof profile.country === 'string' ? profile.country : undefined,
    createdAt: typeof profile.createdAt === 'number' ? profile.createdAt : undefined,
    current_learning_program:
      typeof profile.current_learning_program === 'string'
        ? profile.current_learning_program
        : undefined,
    daily_activities: normalizeDailyActivities(profile.daily_activities),
    email:
      typeof profile.email === 'string' || profile.email === null
        ? profile.email
        : undefined,
    firstname: typeof profile.firstname === 'string' ? profile.firstname : undefined,
    id: profile.id,
    imageUrl: typeof profile.imageUrl === 'string' ? profile.imageUrl : undefined,
    language_default:
      typeof profile.language_default === 'string' ? profile.language_default : undefined,
    lastname: typeof profile.lastname === 'string' ? profile.lastname : undefined,
    learnLanguages: normalizeLearnLanguages(profile.learnLanguages),
    middlename: typeof profile.middlename === 'string' ? profile.middlename : undefined,
    my_daily_goal: normalizeStringArray(profile.my_daily_goal),
    personalization: normalizePersonalization(profile.personalization),
    phone: typeof profile.phone === 'string' ? profile.phone : undefined,
    progress_lessons: mergeLessonProgressEntries({
      legacyEntries: normalizeLessonProgressList(profile.progress_lessons),
      mappedEntries: normalizeLessonProgressMap(profile.progress_lessons_by_id),
    }),
    progress_lessons_by_id: profile.progress_lessons_by_id,
    progress_workbook_lessons: mergeWorkbookProgressEntries({
      legacyEntries: normalizeWorkbookProgressList(
        profile.progress_workbook_lessons,
      ),
      mappedEntries: normalizeWorkbookProgressMap(
        profile.progress_workbook_lessons_by_id,
      ),
    }),
    progress_workbook_lessons_by_id: profile.progress_workbook_lessons_by_id,
    region: typeof profile.region === 'string' ? profile.region : undefined,
    selfstudy_chapter_learning: normalizeSelfStudyChapterLearning(
      profile.selfstudy_chapter_learning,
    ),
    username: typeof profile.username === 'string' ? profile.username : undefined,
  }
}
